import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { useCompanyInfo, useReviews } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function SkydeportePage() {
  const companyInfo = useCompanyInfo();
  const reviews = useReviews();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const images = [
    { src: "/_assets/pxl-20260705-162325452.jpg", title: "Galvaniseret svingport i smedejern med automatik" },
    { src: "/_assets/pxl-20260705-162316724.jpg", title: "Svingport med automatik set forfra" },
    { src: "/_assets/thumbnail-img20250328120707.jpg", title: "Skydeport" },
    { src: "/_assets/thumbnail-img20250423152611.jpg", title: "Skydeport i Danmark" },
    { src: "/_assets/thumbnail-img20250509110537.jpg", title: "Skydeport i træ" },
    { src: "/_assets/thumbnail-img20250908110905.jpg", title: "Skydeport indkørsel" },
    { src: "/_assets/thumbnail-img20250908110929.jpg", title: "Skydeport erhverv" },
  ];

  return (
    <>
      <SEO
        title="Skydeport til Odense og resten af Danmark | Ring nu📱"
        description="En skydeport til din private ejendom eller erhverv? Vi hjælper med at øge sikkerheden og værdien med en skydeport. Få et tilbud i dag."
        ogImage="/_assets/shutterstock-2047144382.jpg"
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/shutterstock-2047144382.jpg", "width=1600,quality=80,format=auto")}
            alt="Skydeport"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#004fa4]/20 text-[#c2ccdf] border border-[#004fa4]/30">
              <i className="fa-sharp fa-solid fa-arrows-left-right" aria-hidden="true"></i>
              <span>Manuel & automatisk</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Den perfekte skydeport til din ejendom
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              Sikkerhed, funktionalitet og stilren adgang til både private ejendomme og erhverv i hele Danmark.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Sikkerhed & værdiforøgelse</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                  Skydeporte der øger sikkerheden
                </h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-base">
                Når det kommer til at vælge den rigtige skydeport til din ejendom, er der mange faktorer at overveje. Som erfaren klejnsmed med mange års erfaring i branchen, forstår Hovendals vigtigheden af både funktionalitet og æstetik. Skydeporte er ikke kun en praktisk løsning til din indkørsel eller garage; de er også en stilfuld tilføjelse, der kan øge sikkerheden og værdien af dit hjem.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                Uanset om du søger en traditionel stålskydeport eller en mere avanceret elektrisk skydeport med automatik, er det vores mål at guide dig igennem de vigtigste overvejelser, så du kan træffe det bedste valg for din ejendom.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="px-6 py-3 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                  <span>Ring os op</span>
                </a>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-envelope" aria-hidden="true"></i>
                  <span>Skriv en mail</span>
                </a>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-slate-100">
              <img
                src={imageUrl("/_assets/thumbnail-img20250328120707.jpg", "width=800,quality=85,format=auto")}
                alt="Skydeport"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Section: Stort udvalg & Fordele */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900">
                Stort udvalg af skydeporte
              </h3>
              <p className="text-slate-700 leading-relaxed text-sm">
                Valget af materiale og design for din skydeport er afgørende. Stålskydeporte er ekstremt populære på grund af deres holdbarhed og evne til at modstå hårdt vejr. Disse porte kan tilpasses i et væld af designs og finish, hvilket gør dem ideelle til både moderne og mere traditionelle ejendomme. Når du vælger design, skal du overveje den arkitektoniske stil af dit hjem samt praktiske aspekter som størrelsen på din indkørsel og den ønskede niveau af privatliv.
              </p>
              <p className="text-slate-700 leading-relaxed text-sm">
                En skræddersyet skydeport, der komplementerer din ejendoms udseende og opfylder dine sikkerhedsbehov, er ikke bare en investering i funktionalitet, men også i dit hjems æstetik. Hovendals ApS hjælper dig med at træffe det rette valg for dig, så du står tilbage med det du drømte om.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-slate-900 text-white shadow-sm space-y-4 flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#c2ccdf]">Fordele ved en skydeport</span>
                <h3 className="text-xl sm:text-2xl font-bold text-white mt-1">
                  En holdbar og æstetisk beslutning
                </h3>
                <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                  Investering i en skydeport er en holdbar og æstetisk beslutning, som du bliver glad for mange år ud i fremtiden.
                </p>
                <p className="text-xs text-slate-400 mt-2">Her er blot 5 af de mange fordele, der er ved at vælge en skydeport:</p>
                <ul className="space-y-2 mt-4 text-sm text-slate-200 font-medium">
                  <li className="flex items-center gap-2.5">
                    <i className="fa-sharp fa-solid fa-circle-check text-[#c2ccdf]" aria-hidden="true"></i>
                    <span>Fleksibilitet i design</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-sharp fa-solid fa-circle-check text-[#c2ccdf]" aria-hidden="true"></i>
                    <span>Lang holdbarhed og levetid</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-sharp fa-solid fa-circle-check text-[#c2ccdf]" aria-hidden="true"></i>
                    <span>Øget ejendomsværdi</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-sharp fa-solid fa-circle-check text-[#c2ccdf]" aria-hidden="true"></i>
                    <span>Øget sikkerhed</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-sharp fa-solid fa-circle-check text-[#c2ccdf]" aria-hidden="true"></i>
                    <span>Manuel eller automatisk skydeport</span>
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-slate-800 text-xs text-slate-300">
                Derudover kan vi også tilbyde <Link to="/komposithegn" className="text-[#c2ccdf] hover:underline">komposithegn</Link> og <Link to="/panelhegn" className="text-[#c2ccdf] hover:underline">panelhegn</Link>.
              </div>
            </div>
          </div>

          {/* Galleri */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Skydeporte galleri
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Klik på et billede for at se forstørret version.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative h-48 rounded-xl overflow-hidden bg-slate-100 cursor-pointer shadow-sm hover:shadow-md transition-all"
                >
                  <img
                    src={imageUrl(img.src, "width=400,quality=80,format=auto")}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <span className="p-2 rounded-full bg-[#143463]/90 text-white text-xs">
                      <i className="fa-sharp fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pris & kontakt */}
          <div className="bg-slate-50 rounded-2xl p-8 sm:p-10 border border-slate-200/80 mb-16 space-y-4">
            <h3 className="text-2xl font-bold text-slate-900">
              Hvad er prisen på en skydeport?
            </h3>
            <p className="text-slate-700 leading-relaxed text-base">
              Prisen på en skydeport varierer afhængigt af materialevalg, størrelse, automatik og andre specifikke tilpasninger. Hovendals ApS tilbyder konkurrencedygtige priser og sikrer, at du får den bedste kvalitet for pengene. Desuden vil en holdbar og vedligeholdelsesvenlig skydeport spare dig for fremtidige reparationsomkostninger.
            </p>
            <p className="text-slate-700 leading-relaxed text-base">
              Hvis du overvejer at installere en skydeport på din ejendom, så tøv ikke med at kontakte os for en snak om dine behov og ønsker. Vi er her for at hjælpe dig med at vælge den perfekte løsning, der passer til både dit budget og din stil.
            </p>
            <p className="text-slate-700 leading-relaxed text-base">
              Vi tilbyder også, <Link to="/kompositlaage" className="text-[#004fa4] hover:underline font-semibold">kompositlåge</Link>, <Link to="/panellaage" className="text-[#004fa4] hover:underline font-semibold">panellåge</Link> og andre produkter, som du kan se mere om.
            </p>

            <h4 className="text-xl font-bold text-slate-900 pt-4">
              Skal du bruge en skydeport? Kontakt os allerede i dag
            </h4>
            <p className="text-slate-700 leading-relaxed text-base">
              Er du interesseret i at høre mere om mulighederne ved at vælge en skydeport, uanset om det er til din private ejendom eller til erhverv, så giv os et kald allerede i dag. Vores team af erfarne fagfolk står klar til at rådgive dig og svare på alle dine spørgsmål.
            </p>
            <p className="text-slate-700 leading-relaxed text-base">
              Ring til os på telefon <a href={`tel:${companyInfo.phoneFormatted}`} className="text-[#004fa4] font-bold hover:underline">{companyInfo.phone}</a> eller send en mail til <a href={`mailto:${companyInfo.email}`} className="text-[#004fa4] font-bold hover:underline">{companyInfo.email}</a>. Du er også velkommen til at udfylde vores kontaktformular, så hører du fra os hurtigst muligt.
            </p>
            <p className="text-slate-800 font-bold text-base">
              Lad os sammen gøre din ejendom både sikrere og mere indbydende.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="px-6 py-2.5 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all"
              >
                Ring os op
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                className="px-6 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all"
              >
                Skriv en mail
              </a>
            </div>
          </div>

          {/* Anmeldelser */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">
              Anbefalinger fra vores kunder
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((r, i) => (
                <div key={i} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-1 text-[#c2ccdf]">
                      {[...Array(5)].map((_, s) => (
                        <i key={s} className="fa-sharp fa-solid fa-star text-sm" aria-hidden="true"></i>
                      ))}
                    </div>
                    <h4 className="font-bold text-slate-900 text-base">{r.title}</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">{r.text}</p>
                  </div>
                  <div className="pt-4 border-t border-slate-100 font-bold text-xs text-slate-900 mt-4">
                    — {r.author}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <QuoteSection />

      {/* Lightbox modal */}
      <LightboxModal
        images={images}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(index) => setLightboxIndex(index)}
      />
    </>
  );
}
